/* eslint-disable max-lines */
import { PrivateRoute } from "@src/components/routing";
import { loadLazily } from "@src/utils/loadLazily";
import { routes } from "@src/appV2/redesign/utils/routes";
import { Redirect, Route, Switch } from "react-router-dom";

import { useWorkplaceMenuPriviligesProviderContext } from "../priviligesProviders/WorkplaceMenuPriviligesProvider";
import { HelpCenterPage } from "@src/appV2/HelpCenter/Page";
import { LegacyRouteWrapper } from "./LegacyRouteWrapper";
import { AppV2Routes } from "@src/appV2/App/Routes";

const FacilityOnboarding = loadLazily(
  async () =>
    await import("@src/containers/facilityOnboarding/FacilityOnboarding").then((module) => ({
      default: module.FacilityOnboarding,
    }))
);

const DailyView = loadLazily(
  async () =>
    await import("@src/appV2/redesign/DailyView/DailyView").then((module) => ({
      default: module.DailyView,
    }))
);

const FacilityDashboard = loadLazily(
  async () =>
    await import("@src/containers/facilityDashboard").then((module) => ({
      default: module.FacilityDashboard,
    }))
);

const ChatPage = loadLazily(
  async () =>
    await import("@src/containers/chat/chat").then((module) => ({
      default: module.ChatPage,
    }))
);

const MyProfessionalsPageWrapper = loadLazily(
  async () =>
    await import(
      "@src/containers/workers/Professionals/MyProfessionals/MyProfessionalsPageWrapper"
    ).then((module) => ({
      default: module.MyProfessionalsPageWrapper,
    }))
);

const WorkersDirectory = loadLazily(
  async () =>
    await import("@src/containers/workplace/WorkersDirectory/WorkersDirectory").then((module) => ({
      default: module.WorkersDirectoryPage,
    }))
);

const WorkersDirectoryWorkerDetails = loadLazily(
  async () =>
    await import("@src/containers/workplace/WorkerDetails/WorkerDetailsPageWrapper").then(
      (module) => ({
        default: module.WorkerDetailsPageWrapper,
      })
    )
);

const WorkplaceWorkerDetails = loadLazily(
  async () =>
    await import("@src/containers/workers/WorkerDetails/WorkerDetailsPageWrapper").then(
      (module) => ({
        default: module.WorkerDetailsPageWrapper,
      })
    )
);

const PlacementCandidateDetailsPage = loadLazily(
  async () =>
    await import("@src/appV2/PlacementCandidates/PlacementCandidateDetailsPage").then((module) => ({
      default: module.PlacementCandidateDetailsPage,
    }))
);

const WorkplaceSignContract = loadLazily(
  async () =>
    await import("@src/containers/workplaceSignContract/WorkplaceSignContract").then((module) => ({
      default: module.WorkplaceSignContractPage,
    }))
);

const WorkplaceBlockShifts = loadLazily(
  async () =>
    await import("@src/containers/workplaceBlockShifts/WorkplaceBlockShifts").then((module) => ({
      default: module.WorkplaceBlockShifts,
    }))
);

const FacilityWorkerInstructions = loadLazily(
  async () =>
    await import("@src/containers/facilityWorkerInstructions").then((module) => ({
      default: module.FacilityWorkerInstructions,
    }))
);

const FacilityRulesPage = loadLazily(
  async () =>
    await import("@src/appV2/FacilityRules/Page").then((module) => ({
      default: module.FacilityRulesPage,
    }))
);

const InterviewSchedulePage = loadLazily(
  async () =>
    await import("@src/appV2/InterviewSchedule/Page").then((module) => ({
      default: module.InterviewSchedulePage,
    }))
);

const UnverifiedPlacementCandidateApplicationPage = loadLazily(
  async () =>
    await import(
      "@src/appV2/UnverifiedPlacementCandidateApplication/UnverifiedPlacementCandidateApplication"
    ).then((module) => ({
      default: module.UnverifiedPlacementCandidateApplicationPage,
    }))
);

const WorkerDocuments = loadLazily(
  async () =>
    await import("@src/appV2/WorkerDocuments/WorkerDocumentsView").then((module) => ({
      default: module.WorkerDocumentsView,
    }))
);

const TimesheetView = loadLazily(
  async () =>
    await import("@src/containers/accountManagement/invoice/timesheetView").then((module) => ({
      default: module.TimesheetView,
    }))
);

const JobsLandingPage = loadLazily(
  async () =>
    await import("@src/appV2/JobsLandingPage/Page").then((module) => ({
      default: module.JobsLandingPage,
    }))
);

const ManageJobsTabs = loadLazily(
  async () =>
    await import("@src/appV2/ManageJobs/ManageJobsTabs").then((module) => ({
      default: module.ManageJobsTabs,
    }))
);

const PlacementCandidates = loadLazily(
  async () =>
    await import("@src/appV2/PlacementCandidates/Page").then((module) => ({
      default: module.PlacementCandidatesPage,
    }))
);

const InterviewSettings = loadLazily(
  async () =>
    await import("@src/appV2/InterviewSettings/Page").then((module) => ({
      default: module.InterviewSettingsPage,
    }))
);

const WorkplaceCharting = loadLazily(
  async () =>
    await import("@src/appV2/Charting/components/ChartingCompletionReport").then((module) => ({
      default: module.ChartingCompletionReport,
    }))
);

const AnalyticsDashboard = loadLazily(
  async () =>
    await import("@src/appV2/Analytics/components/AnalyticsDashboard").then((module) => ({
      default: module.AnalyticsDashboard,
    }))
);

const SpendingControlsPage = loadLazily(
  async () =>
    await import("@src/appV2/Corporate/SpendingControls/Page").then((module) => ({
      default: module.SpendingControlsPage,
    }))
);

const WorkplaceSpendingControlsPage = loadLazily(
  async () =>
    await import(
      "@src/appV2/redesign/SpendingControls/WorkplaceSpendingControls/WorkplaceSpendingControls"
    ).then((module) => ({
      default: module.WorkplaceSpendingControls,
    }))
);

const LiveMapView = loadLazily(
  async () =>
    await import("@src/containers/liveMapView").then((module) => ({
      default: module.LiveMapView,
    }))
);

const MyAccount = loadLazily(
  async () =>
    await import("@src/containers/myAccount").then((module) => ({
      default: module.MyAccount,
    }))
);

const TeamMembers = loadLazily(
  async () =>
    await import("@src/containers/teamMembers").then((module) => ({
      default: module.TeamMembers,
    }))
);

const FacilitySettings = loadLazily(
  async () =>
    await import("@src/containers/facilityDashboard/FacilitySettings/Page").then((module) => ({
      default: module.FacilitySettingsPage,
    }))
);

const FacilityTimeClockSettings = loadLazily(
  async () =>
    await import("@src/containers/workplaceSettings").then((module) => ({
      default: module.CustomSettings,
    }))
);

const FacilityUpcomingChargesV2 = loadLazily(
  async () =>
    await import("@src/appV2/FacilityUpcomingCharges").then((module) => ({
      default: module.FacilityUpcomingCharges,
    }))
);

const FacilityInvoiceHistory = loadLazily(
  async () =>
    await import("@src/appV2/FacilityInvoiceHistory").then((module) => ({
      default: module.FacilityInvoiceHistory,
    }))
);

const ContractTerms = loadLazily(
  async () =>
    await import("@src/containers/contractTerms").then((module) => ({
      default: module.ContractTerms,
    }))
);

const WorkplaceReferAndEarn = loadLazily(
  async () =>
    await import("@src/containers/workplaceReferAndEarn/WorkplaceReferAndEarn").then((module) => ({
      default: module.WorkplaceReferAndEarn,
    }))
);

const FacilityInvoiceDispute = loadLazily(
  async () =>
    await import("@src/appV2/FacilityInvoiceDispute").then((module) => ({
      default: module.FacilityInvoiceDisputeContainer,
    }))
);

const FacilityInvoiceDisputeRedirect = loadLazily(
  async () =>
    await import("@src/appV2/FacilityInvoiceDispute/FacilityInvoiceDisputeRedirect").then(
      (module) => ({
        default: module.FacilityInvoiceDisputeRedirect,
      })
    )
);

const PDFGenerator = loadLazily(
  async () =>
    await import("@src/containers/accountManagement/invoice/pdfGenerator").then((module) => ({
      default: module.PDFGenerator,
    }))
);

const TimesheetViewRedirect = loadLazily(
  async () =>
    await import("@src/containers/accountManagement/invoice/timesheetViewRedirect").then(
      (module) => ({
        default: module.TimesheetViewRedirect,
      })
    )
);

const FacilityUserProfile = loadLazily(
  async () =>
    await import("@src/containers/userProfile/facility").then((module) => ({
      default: module.FacilityUserProfile,
    }))
);

export function WorkplaceRoutes(): JSX.Element {
  const workplaceMenuPriviliges = useWorkplaceMenuPriviligesProviderContext();

  return (
    <Switch>
      {/* Redesigned components without Legacy Wrapper */}
      <PrivateRoute
        path={routes.dailyView}
        component={DailyView}
        isAuthorized={workplaceMenuPriviliges.dailyView}
      />

      {/* Legacy components with Legacy Wrapper */}
      <LegacyRouteWrapper>
        <PrivateRoute
          exact
          path={routes.facilityOnboarding}
          component={FacilityOnboarding}
          isAuthorized={workplaceMenuPriviliges.workplaceOnboarding}
        />
        <PrivateRoute
          path={routes.facilityDashboard}
          component={FacilityDashboard}
          isAuthorized={workplaceMenuPriviliges.calendar}
        />
        <PrivateRoute
          path={routes.facilityChatWithAgent}
          component={ChatPage}
          isAuthorized={workplaceMenuPriviliges.chat}
        />
        <PrivateRoute
          exact
          path={routes.workplaceWorkerDetails}
          component={WorkplaceWorkerDetails}
          isAuthorized={workplaceMenuPriviliges.routeAuth?.isWorkplaceOrAdmin}
        />
        <PrivateRoute
          exact
          path={routes.workplaceWorkers}
          component={MyProfessionalsPageWrapper}
          isAuthorized={workplaceMenuPriviliges.professionalsSection?.myProfessionals}
        />
        <PrivateRoute
          exact
          path={routes.workersDirectoryWorkerDetails}
          component={WorkersDirectoryWorkerDetails}
          isAuthorized={workplaceMenuPriviliges.routeAuth?.hasPermissionToAccessWorkerDirectory}
        />
        <PrivateRoute
          exact
          path={routes.workersDirectory}
          component={WorkersDirectory}
          isAuthorized={workplaceMenuPriviliges.professionalsSection?.browseProfessionals}
        />
        <PrivateRoute
          exact
          path={routes.document}
          component={WorkerDocuments}
          isAuthorized={workplaceMenuPriviliges.routeAuth?.isDocumentAuthorized}
        />
        <PrivateRoute
          exact
          path={routes.workplaceBlockShifts}
          component={WorkplaceBlockShifts}
          isAuthorized={workplaceMenuPriviliges.routeAuth?.hasPermissionToAccessWorkerDirectory}
        />
        <PrivateRoute
          exact
          path={routes.jobsLandingPage}
          component={JobsLandingPage}
          isAuthorized={workplaceMenuPriviliges.permanentHiringSection?.placementsLandingPage}
        />
        <PrivateRoute
          exact
          path={routes.manageJobs}
          component={ManageJobsTabs}
          isAuthorized={workplaceMenuPriviliges.permanentHiringSection?.applicationAndJobs}
        />
        <PrivateRoute
          exact
          path={routes.workplaceHireProfessionalDetails}
          component={PlacementCandidateDetailsPage}
          isAuthorized={workplaceMenuPriviliges.permanentHiringSection?.browseTalentPool}
        />
        <PrivateRoute
          exact
          path={routes.workplaceHireProfessionals}
          component={PlacementCandidates}
          isAuthorized={workplaceMenuPriviliges.permanentHiringSection?.browseTalentPool}
        />
        <PrivateRoute
          exact
          path={routes.interviewSettings}
          component={InterviewSettings}
          isAuthorized={workplaceMenuPriviliges.permanentHiringSection?.interviewAvailability}
        />
        <PrivateRoute
          exact
          path={routes.interviewSchedule}
          component={InterviewSchedulePage}
          isAuthorized={workplaceMenuPriviliges.permanentHiringSection?.interviewAvailability}
        />
        <Route
          exact
          path={routes.workplaceJobApplicantDetails}
          component={UnverifiedPlacementCandidateApplicationPage}
        />
        <PrivateRoute
          exact
          path={routes.workplaceCharting}
          component={WorkplaceCharting}
          isAuthorized={workplaceMenuPriviliges.charting}
        />
        <PrivateRoute
          exact
          path={routes.workplaceAnalytics}
          component={AnalyticsDashboard}
          isAuthorized={workplaceMenuPriviliges.analyticsSection}
        />
        <PrivateRoute
          exact
          path={routes.corporateSpendingControls}
          component={SpendingControlsPage}
          isAuthorized={workplaceMenuPriviliges.corporateCenterSection?.spendingControls}
        />
        <PrivateRoute
          exact
          path={routes.workplaceSpendingControls}
          component={WorkplaceSpendingControlsPage}
          isAuthorized={workplaceMenuPriviliges.billingSection?.workplaceSpendingControls}
        />
        <PrivateRoute
          exact
          path={routes.facilityLiveWorkerETA}
          component={LiveMapView}
          isAuthorized={workplaceMenuPriviliges.routeAuth?.hasDefaultWorkplacePermissions}
        />
        <PrivateRoute
          exact
          path={routes.myAccount}
          component={MyAccount}
          isAuthorized={workplaceMenuPriviliges.managementSection?.myAccount}
        />
        <PrivateRoute
          exact
          path={routes.workplaceUsers}
          component={TeamMembers}
          isAuthorized={workplaceMenuPriviliges.managementSection?.teamMembers}
        />
        <PrivateRoute
          exact
          path={routes.facilitySettings}
          component={FacilitySettings}
          isAuthorized={workplaceMenuPriviliges.managementSection?.settings}
        />
        <PrivateRoute
          exact
          path={routes.facilityTimeclockSettings}
          component={FacilityTimeClockSettings}
          isAuthorized={workplaceMenuPriviliges.managementSection?.timeclockingSettings}
        />
        <PrivateRoute
          exact
          path={routes.facilityInvoiceUpcomingCharges}
          component={FacilityUpcomingChargesV2}
          isAuthorized={workplaceMenuPriviliges.billingSection?.upcomingCharges}
        />
        <PrivateRoute
          exact
          path={routes.facilityInvoiceHistory}
          component={FacilityInvoiceHistory}
          isAuthorized={workplaceMenuPriviliges.billingSection?.invoiceHistory}
        />
        <PrivateRoute
          exact
          path={routes.facilityInvoiceContractTerms}
          component={ContractTerms}
          isAuthorized={workplaceMenuPriviliges.billingSection?.contractTerms}
        />
        <PrivateRoute
          exact
          path={routes.helpCenter}
          component={HelpCenterPage}
          isAuthorized={workplaceMenuPriviliges.helpCenter}
        />
        <PrivateRoute
          exact
          path={routes.workplaceReferAndEarn}
          component={WorkplaceReferAndEarn}
          isAuthorized={workplaceMenuPriviliges.referAndEarn}
        />
        <PrivateRoute
          exact
          path={routes.workplaceSignContract}
          component={WorkplaceSignContract}
          isAuthorized={workplaceMenuPriviliges.calendar}
        />
        <Route
          exact
          path={routes.facilityWorkerInstructions}
          component={
            workplaceMenuPriviliges.workplaceRulesQuiz
              ? FacilityRulesPage
              : FacilityWorkerInstructions
          }
        />
        <Route exact path={routes.timesheet} component={TimesheetView} />
        <PrivateRoute
          exact
          path={routes.facilityInvoiceDispute}
          component={FacilityInvoiceDispute}
          isAuthorized={workplaceMenuPriviliges.routeAuth?.isFacilityInvoiceDisputeAuthorized}
        />
        <PrivateRoute
          exact
          path={routes.facilityInvoiceDisputeRedirect}
          component={FacilityInvoiceDisputeRedirect}
          isAuthorized={workplaceMenuPriviliges.routeAuth?.isFacilityInvoiceDisputeAuthorized}
        />
        <PrivateRoute
          exact
          path={routes.timesheetPdf}
          component={PDFGenerator}
          isAuthorized={workplaceMenuPriviliges.routeAuth?.isInvoiceViewAuthorized}
        />
        <PrivateRoute
          exact
          path={routes.timesheetRedirect}
          component={TimesheetViewRedirect}
          isAuthorized={workplaceMenuPriviliges.routeAuth?.hasDefaultWorkplacePermissions}
        />
        <PrivateRoute
          exact
          path={routes.facilityProfile}
          component={FacilityUserProfile}
          isAuthorized={workplaceMenuPriviliges.routeAuth?.isWorkplaceOrAdmin}
        />
      </LegacyRouteWrapper>

      {/* AppV2 routes (Home Health, etc.) */}
      <Route path={routes.appV2}>
        <AppV2Routes />
      </Route>

      {/* Catch-all: redirect unrecognized workplace routes to the default workplace page */}
      <Route>
        <Redirect to={routes.facilityDashboard} />
      </Route>
    </Switch>
  );
}
/* eslint-enable max-lines */
